import React, { useMemo } from 'react';
import { Stack, StackProps } from '@mui/material';
import { RequisitionChartStatusDotVariant } from 'models/enum';
import { getStageColor } from 'modules/hiring/utilities';
import { IStageMetaData } from 'modules/hiring/models/myOpenRequisition.model';
import { areStringsEqual } from 'utilities/utilityFunctions';

interface IRequisitionChartStatusDotProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLSpanElement>,
    HTMLSpanElement
  > {
  id: string;
  color: string;
  variant: RequisitionChartStatusDotVariant;
  size?: number;
}

export interface IRequisitionChartProps extends StackProps {
  id: string;
  stages: IStageMetaData[];
  currentStage: string;
  currentStep: string;
}

const RequisitionChartStatusDot: React.FC<IRequisitionChartStatusDotProps> = ({
  color,
  variant,
  size,
  id,
  ...props
}) => {
  const isCombined = variant === RequisitionChartStatusDotVariant.COMBINED;
  const isSolid = variant === RequisitionChartStatusDotVariant.SOLID;
  if (!size) {
    size = isCombined ? 22 : 16;
  }

  return (
    <span
      data-variant={variant}
      id={id}
      data-testid={id}
      {...props}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: isSolid ? color : 'white',
        border: !isSolid ? `2px solid ${color}` : '',
        boxSizing: 'border-box',
        position: 'relative',
        ...props.style,
      }}
    >
      {isCombined && (
        <RequisitionChartStatusDot
          id={`${id}-inner-dot`}
          color={color}
          variant={RequisitionChartStatusDotVariant.SOLID}
          size={11}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      )}
    </span>
  );
};

const RequisitionChart: React.FC<IRequisitionChartProps> = ({
  id,
  stages,
  currentStage,
  currentStep,
  ...props
}) => {
  const gradientPercent = useMemo(() => {
    const currentStageSteps =
      stages.find((stage) => areStringsEqual(stage.name, currentStage))?.steps ??
      [];
    const countOfStep = currentStageSteps.length;
    if (countOfStep <= 0) return 0;
    const stepIndex = currentStageSteps.findIndex((step) =>
      areStringsEqual(step, currentStep)
    );
    const safeIndex = stepIndex < 0 ? 0 : stepIndex;
    const rawCount = Math.round(100 * ((0.5 + safeIndex) / countOfStep));
    return Math.max(0, Math.min(100, rawCount));
  }, [stages, currentStage, currentStep]);

  let foundStage = false;

  return (
    <Stack
      id={id}
      data-testid={id}
      direction='row'
      justifyContent='space-between'
      alignItems='center'
      spacing='0.25rem'
      {...props}
      sx={{ width: '100%', height: 24, ...props.sx }}
    >
      {stages.map((stage, i) => {
        const color = getStageColor(stage.name, stages);
        let foundStep = false;
        if (!foundStage && areStringsEqual(stage.name, currentStage)) {
          foundStage = true;
          return (
            <Stack
              key={`${i}-${stage.name}`}
              id={`${stage.name}-stack`}
              data-testid={`${stage.name}-stack`}
              flexDirection='row'
              alignItems='center'
              justifyContent='space-around'
              flex={1}
              style={{
                background: `linear-gradient(to right, ${color} ${gradientPercent}%, white ${gradientPercent}%)`,
                maxHeight: 8,
                borderRadius: 4,
                border: `2px solid ${color}`,
                boxSizing: 'border-box',
              }}
            >
              {stage.steps.map((step) => {
                if (!foundStep && areStringsEqual(step, currentStep)) {
                  foundStep = true;
                  return (
                    <RequisitionChartStatusDot
                      id={`${step}-dot`}
                      key={step}
                      variant={RequisitionChartStatusDotVariant.COMBINED}
                      color={color}
                    />
                  );
                } else {
                  return (
                    <RequisitionChartStatusDot
                      key={stage.name + `${step}-dot`}
                      id={`${step}-dot`}
                      variant={
                        foundStep
                          ? RequisitionChartStatusDotVariant.HALO
                          : RequisitionChartStatusDotVariant.SOLID
                      }
                      color={color}
                    />
                  );
                }
              })}
            </Stack>
          );
        } else {
          return (
            <RequisitionChartStatusDot
              id={`${stage.name}-dot`}
              key={stage.name + `${i}-dot`}
              variant={
                foundStage
                  ? RequisitionChartStatusDotVariant.HALO
                  : RequisitionChartStatusDotVariant.SOLID
              }
              color={color}
            />
          );
        }
      })}
    </Stack>
  );
};

export default RequisitionChart;
